import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { BookingButton } from "@/components/BookingButton";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { IMAGES } from "@/lib/motion";
import { Reveal, Overline } from "@/components/Reveal";
import { useSEO } from "@/lib/useSEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROFILES = [
  "État / Institution publique",
  "Investisseur",
  "Bailleur de fonds",
  "Porteur de projet",
  "Autre",
];

export default function Contact() {
  useSEO({
    title: "Contact",
    description:
      "Échangez avec HERUS Axiom sur votre projet d'infrastructure, de PPP ou de transition énergétique. Contact confidentiel.",
    keywords: "contact, échanger, projet, infrastructures, PPP, transition énergétique, conseil",
    path: "/contact",
  });
  const [params] = useSearchParams();
  const [form, setForm] = useState({ name: "", email: "", organization: "", profile: "", message: params.get("message") || "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Veuillez renseigner votre nom, votre email et votre message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Votre message a bien été transmis. Nous vous répondrons rapidement.");
      setForm({ name: "", email: "", organization: "", profile: "", message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue. Merci de réessayer.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-4 text-[#F9F6F0] placeholder:text-[#F9F6F0]/30 focus:border-[#C5A059] focus:outline-none transition-colors duration-300 font-light";

  return (
    <div data-testid="contact-page" className="bg-[#0A0A0A] min-h-screen">
      <section className="pt-40 lg:pt-52 pb-24 mx-auto max-w-[1600px] px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
        {/* Left */}
        <div className="lg:col-span-5">
          <Reveal><Overline>Contact</Overline></Reveal>
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            className="mt-8 font-serif-display font-light leading-[0.98] tracking-tight text-[#F9F6F0] text-4xl lg:text-6xl"
          >
            Les décisions importantes commencent toujours par une <span className="italic text-[#C5A059]">conversation.</span>
          </motion.h1>
          <Reveal i={2}>
            <p className="mt-8 max-w-md text-[#F9F6F0]/70 font-light leading-relaxed">
              Parce que chaque organisation, chaque territoire et chaque projet présentent des défis
              spécifiques, nous privilégions un dialogue fondé sur l'écoute, la compréhension et la
              confidentialité. Partagez-nous vos enjeux ; nous serons heureux d'échanger avec vous.
            </p>
          </Reveal>

          <div className="mt-14 space-y-8">
            <Reveal i={3}>
              <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059] mb-2">Email</p>
              <a href="mailto:leonie.honban@herusaxiom.com" data-testid="contact-email" className="text-[#F9F6F0]/85 font-light hover:text-[#C5A059] transition-colors whitespace-nowrap">leonie.honban@herusaxiom.com</a>
            </Reveal>
            <Reveal i={4}>
              <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059] mb-2">Téléphone</p>
              <a href="tel:+33618470960" data-testid="contact-phone" className="text-[#F9F6F0]/85 font-light hover:text-[#C5A059] transition-colors">06 18 47 09 60</a>
            </Reveal>
            <Reveal i={5}>
              <p className="text-xs uppercase tracking-[0.25em] text-[#C5A059] mb-2">Adresse</p>
              <p className="text-[#F9F6F0]/85 font-light">66 avenue des Champs-Élysées, 75008 Paris</p>
            </Reveal>
            <BookingButton testId="contact-booking-btn" />
          </div>

          <Reveal i={6} className="mt-12 hidden lg:block relative overflow-hidden grain border border-white/10 aspect-[16/9]">
            <img src={IMAGES.structure} alt="Architecture contemporaine" className="absolute inset-0 h-full w-full object-cover opacity-70" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/70 to-transparent" />
          </Reveal>
        </div>

        {/* Form */}
        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal i={1} className="border border-white/10 p-8 lg:p-12">
            {sent ? (
              <div data-testid="contact-success" className="py-16 text-center flex flex-col items-center">
                <CheckCircle2 size={48} className="text-[#C5A059]" />
                <h3 className="mt-6 font-serif-display text-3xl text-[#F9F6F0]">Message transmis</h3>
                <p className="mt-3 text-[#F9F6F0]/60 font-light max-w-sm">
                  Merci pour votre confiance. Nous reviendrons vers vous dans les meilleurs délais.
                </p>
                <button
                  data-testid="contact-reset"
                  onClick={() => setSent(false)}
                  className="mt-8 text-sm uppercase tracking-[0.2em] text-[#C5A059] border-b border-[#C5A059] pb-1"
                >
                  Envoyer un autre message
                </button>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#F9F6F0]/40">Nom *</label>
                    <input data-testid="input-name" value={form.name} onChange={update("name")} className={inputCls} placeholder="Votre nom" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#F9F6F0]/40">Email *</label>
                    <input data-testid="input-email" type="email" value={form.email} onChange={update("email")} className={inputCls} placeholder="vous@organisation.com" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-8">
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#F9F6F0]/40">Organisation</label>
                    <input data-testid="input-organization" value={form.organization} onChange={update("organization")} className={inputCls} placeholder="Institution / entreprise" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.2em] text-[#F9F6F0]/40">Profil</label>
                    <select data-testid="input-profile" value={form.profile} onChange={update("profile")} className={`${inputCls} appearance-none cursor-pointer`}>
                      <option value="" className="bg-[#0A0A0A]">Sélectionner…</option>
                      {PROFILES.map((p) => (
                        <option key={p} value={p} className="bg-[#0A0A0A]">{p}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-[0.2em] text-[#F9F6F0]/40">Votre projet *</label>
                  <textarea data-testid="input-message" value={form.message} onChange={update("message")} rows={4} className={`${inputCls} resize-none`} placeholder="Décrivez votre contexte et vos enjeux…" />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={loading}
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-[#C5A059] text-[#0A0A0A] text-sm uppercase tracking-[0.2em] px-10 py-4 hover:bg-[#F9F6F0] transition-colors duration-500 disabled:opacity-60"
                >
                  {loading ? <Loader2 size={18} className="animate-spin" /> : <>Prendre contact<ArrowRight size={18} className="transition-transform duration-500 group-hover:translate-x-1.5" /></>}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </section>
    </div>
  );
}
